/* eslint-disable */
import { faSend, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput, Modal, ModalVariant, TextArea } from '@patternfly/react-core';
import React, { useState, useEffect, useRef } from 'react';
import { ChatActions } from '../../api/lambda/ChatActions';
import {
	ChatMessage,
	FilterItem,
	InovationChatRequest,
	InovationChatResponse,
	OrderByItem,
	Reasoning,
} from '../../api/types';
import { useApplication } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import Markdown from './Markdown';
import { Dataframe } from '../../api/dataframes/Dataframes';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useHighChart } from '../../hooks/HighChartHooks';
import { SavedChat } from '../../routes/discover/SavedChats';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { capitalizeFirstLetter } from '../../utilities';
import Highcharts from 'highcharts';

export interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
	reasoning?: Reasoning[];
	chart?: any;
	aiChartSetup?: any;
	title?: any;
}

interface ChatInterfaceProps {
	context?: string;
	contextId?: string | number; // When this changes, reset the chat
	/** Render messages externally in a shared thread instead of inside ChatInterface */
	externalThread?: boolean;
	/** Called when messages change, so parent can render them in a shared thread */
	onMessagesChange?: (messages: Message[]) => void;
	/** Called when isThinking changes */
	onThinkingChange?: (isThinking: boolean) => void;
	/** Whether the modal is managed externally */
	showModal?: boolean;
	onModalClose?: () => void;
	/** Extra content to render before messages in the modal (e.g., insights card) */
	modalHeaderContent?: React.ReactNode;
	/** Disable input until context is ready */
	disabled?: boolean;
	/** Called once when the user first types into the input */
	onFirstInteraction?: () => void;
	/** Auto-submit this message on mount (used by Discover "Chat about this") */
	autoSubmitMessage?: string;
	// indicates if the chat is being used for a single fullscreen page
	isSinglePage?: boolean;
	/** When true, use the Innovation AI API instead of the normal chat */
	useInnovationAI?: boolean;
	/** Called after auto-submit completes so parent can clear URL params */
	onAutoSubmitted?: () => void;

	// For analyst page only
	sessionId?: string; // Pass session_id to load a saved chat
	onSaveChat?: (sessionId: string, jobId: string, title: string) => void;
	canSaveChat?: boolean; // Only true on analyst page
}

/** Renders chat messages (for use in external threads) */
const ChartMessage: React.FC<{ chart: any; title?: any; text?: string }> = ({
	chart,
	title,
	text,
}) => {
	const { options, setOptions, chartRender } = useHighChart(() => {}, false as boolean);
	useEffect(() => {
		if (chart) {
			setOptions(chart);
		}
	}, [chart, setOptions]);

	return (
		<div style={{ width: '100%' }}>
			{text && text.length > 0 && (
				<div style={{ marginBottom: '8px' }}>
					<Markdown>{text}</Markdown>
					<br />
					<br />
				</div>
			)}
			<div style={{ height: 320, width: '100%' }}>{chartRender}</div>
		</div>
	);
};

export const ChatMessages: React.FC<{
	messages: Message[];
	isThinking: boolean;
	useInnovationAI?: boolean;
}> = ({ messages, isThinking, useInnovationAI }) => (
	<>
		{messages.map((msg) => (
			<div
				key={msg.id}
				className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
			>
				{msg.sender === 'bot' ? (
					msg.chart ? (
						<ChartMessage
							chart={msg.chart}
							title={msg.title}
							text={msg.text}
						/>
					) : (
						<Markdown>{msg.text}</Markdown>
					)
				) : (
					msg.text
				)}
				{msg.sender === 'bot' && msg.reasoning && msg.reasoning.length > 0 && (
					<div className="chat-reasoning">
						<br />
						<details>
							<summary style={{ cursor: 'pointer' }}>
								<i>Reasoning</i>
							</summary>
							<ul>
								{msg.reasoning.map((step, idx) => (
									<li key={idx}>
										<strong>{step.step}</strong>
										{step.details && Object.keys(step.details).length > 0 && (
											<table
												style={{
													marginTop: '6px',
													borderCollapse: 'collapse',
													fontSize: '14px',
													width: '100%',
												}}
											>
												<tbody>
													{Object.entries(step.details).map(
														([key, value]) => (
															<tr key={key}>
																<td
																	style={{
																		padding: '2px 8px 2px 0',
																		fontWeight: 500,
																		whiteSpace: 'nowrap',
																		verticalAlign: 'top',
																	}}
																>
																	{capitalizeFirstLetter(
																		key.replace(/_/g, ' ')
																	)}
																</td>
																<td
																	style={{
																		padding: '2px 0',
																		verticalAlign: 'top',
																		fontSize: '14px',
																	}}
																>
																	{Array.isArray(value)
																		? (value as any[]).every(
																				(v) =>
																					typeof v ===
																					'string'
																		  )
																			? (
																					value as string[]
																			  ).join(', ')
																			: (
																					value as (
																						| FilterItem
																						| OrderByItem
																					)[]
																			  ).map((item, i) => (
																					<span
																						key={i}
																						style={{
																							display:
																								'block',
																						}}
																					>
																						{Object.entries(
																							item
																						)
																							.map(
																								([
																									k,
																									v,
																								]) =>
																									`${k}: ${v}`
																							)
																							.join(
																								' | '
																							)}
																					</span>
																			  ))
																		: String(value)}
																</td>
															</tr>
														)
													)}
												</tbody>
											</table>
										)}
									</li>
								))}
							</ul>
						</details>
					</div>
				)}
			</div>
		))}
		{isThinking && (
			<div className="chat-bubble bot thinking">
				<div className="typing-indicator">
					<span className="dot"></span>
					<span className="dot"></span>
					<span className="dot"></span>
				</div>
			</div>
		)}
	</>
);

/** Renders the pinned chat input area */
export const ChatInput: React.FC<{
	input: string;
	setInput: (val: string) => void;
	onSend: () => void;
	isThinking: boolean;
	placeholder?: string;
	disabled?: boolean;
	isModalOpen?: boolean;
	hideSendButton?: boolean;
}> = ({
	input,
	setInput,
	onSend,
	isThinking,
	placeholder,
	disabled,
	isModalOpen,
	hideSendButton,
}) => {
	const measureRef = useRef<HTMLSpanElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const textInputRef = useRef<any>(null);
	const textAreaRef = useRef<any>(null);
	const [isMultiline, setIsMultiline] = useState(false);

	useEffect(() => {
		if (!measureRef.current || !containerRef.current) return;

		const measure = () => {
			if (!measureRef.current || !containerRef.current) return;

			// if user inserted newline, stay multiline
			if (input.includes('\n')) {
				setIsMultiline(true);
				return;
			}

			measureRef.current.textContent = input || ' ';

			const textWidth = measureRef.current.scrollWidth;
			const containerWidth = containerRef.current.clientWidth;

			const ignore = containerWidth == 370 && isModalOpen;
			const offset = isModalOpen ? 183 : 100;

			if (!ignore) {
				setIsMultiline(textWidth + offset > containerWidth);
			}
		};

		requestAnimationFrame(measure);
	}, [input, isModalOpen]);

	useEffect(() => {
		setIsMultiline(false);
	}, [isModalOpen]);

	useEffect(() => {
		if (isModalOpen) {
			textAreaRef.current?.blur();
			textAreaRef.current?.focus?.();

			setTimeout(() => {
				textInputRef.current?.blur();
				textInputRef.current?.focus?.();
				const len = textInputRef.current.value.length;
				textInputRef.current.setSelectionRange(len, len);
			}, 50);
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (!isMultiline) {
			// wait for DOM swap
			requestAnimationFrame(() => {
				textInputRef.current?.focus?.();
			});
		} else {
			// wait for DOM swap
			requestAnimationFrame(() => {
				textAreaRef.current?.focus?.();
				const len = textAreaRef.current.value.length;
				textAreaRef.current.setSelectionRange(len, len);
			});
		}
	}, [isMultiline]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	const handleKeyPressInput = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.shiftKey) {
			e.preventDefault();
			setIsMultiline(true);
			setInput(input + '\n');
			return;
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	return (
		<div
			className="chat-input-group"
			ref={containerRef}
		>
			{/* hidden measurement span */}
			<span
				ref={measureRef}
				style={{
					position: 'absolute',
					visibility: 'hidden',
					whiteSpace: 'pre',
					fontSize: '14px',
					fontFamily: 'inherit',
					letterSpacing: 'inherit',
				}}
			/>

			{isMultiline ? (
				<TextArea
					ref={textAreaRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPress}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
					autoResize
					rows={2}
					className={`taChatMessagePrompt ${isModalOpen ? 'chat-modal-open' : ''}`}
				/>
			) : (
				<TextInput
					ref={textInputRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPressInput}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
				/>
			)}

			{!hideSendButton && (
				<Button
					onClick={onSend}
					isDisabled={!input.trim() || isThinking || disabled}
					variant="primary"
					className="chat-send-btn"
				>
					<FontAwesomeIcon icon={faSend} />
				</Button>
			)}
		</div>
	);
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
	context = '',
	contextId,
	externalThread = false,
	onMessagesChange,
	onThinkingChange,
	showModal,
	onModalClose,
	modalHeaderContent,
	disabled,
	onFirstInteraction,
	autoSubmitMessage,
	isSinglePage,
	useInnovationAI,
	onAutoSubmitted,
	sessionId,
	onSaveChat,
	canSaveChat = false,
}) => {
	const { zoneSettings, measures, user } = useApplication();
	const { setCurrentSelectedDataframe } = useCommonStoreContext();
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const hasInteractedRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const [isThinking, setIsThinking] = useState(false);
	const [internalShowModal, setInternalShowModal] = useState(false);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const modalChatContainerRef = useRef<HTMLDivElement | null>(null);

	const hideSend = Boolean(useInnovationAI && isSinglePage);

	const isModalOpen = showModal !== undefined ? showModal : internalShowModal;
	const handleModalClose = onModalClose ?? (() => setInternalShowModal(false));

	const scrollToBottom = () => {
		const container = isModalOpen ? modalChatContainerRef.current : chatContainerRef.current;
		if (container) {
			container.scrollTop = container.scrollHeight;
		}
	};

	// Reset chat when graph context changes
	const prevSessionIdRef = useRef(sessionId);

	useEffect(() => {
		const prev = prevSessionIdRef.current;
		prevSessionIdRef.current = sessionId;

		// New chat: sessionId changes to a fresh timestamp, prev was also defined
		// Loaded chat: sessionId changes to a real session id
		// First mount: prev === sessionId (both start as the initial value) → skip
		if (prev === sessionId) return;

		// Only reset if this is genuinely a new blank session (no contextId/jobId)
		// Loading a saved chat should NOT reset — the load effect handles that
		if (!contextId) {
			setMessages([]);
			setInput('');
			hasAutoSubmittedRef.current = false;
		}
	}, [sessionId]);

	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages]);

	useEffect(() => {
		onThinkingChange?.(isThinking);
	}, [isThinking]);

	useEffect(() => {
		if (messages.length > 0 || isThinking) {
			scrollToBottom();
		}
	}, [messages, isThinking, isModalOpen]);

	const prevMessageCountRef = useRef(0);

	// Set isThinking to false only after new bot messages are rendered
	useEffect(() => {
		// If message count increased and last message is from bot, disable thinking
		if (
			messages.length > prevMessageCountRef.current &&
			messages[messages.length - 1]?.sender === 'bot' &&
			isThinking
		) {
			const timer = setTimeout(() => {
				setIsThinking(false);
			}, 100);
			prevMessageCountRef.current = messages.length;
			return () => clearTimeout(timer);
		}
		prevMessageCountRef.current = messages.length;
	}, [messages]);

	const sanitizeHighchartsConfig = (config: any): any => {
		if (!config || typeof config !== 'object') return config;

		const functionKeys = [
			'formatter',
			'labelFormatter',
			'tooltipFormatter',
			'pointFormatter',
			'headerFormatter',
			'render',
			'callback',
			'complete',
			'step',
		];

		for (const key of Object.keys(config)) {
			if (functionKeys.includes(key) && typeof config[key] === 'string') {
				try {
					const fn = new Function('Highcharts', `return (${config[key]})`)(Highcharts);
					if (typeof fn === 'function') {
						config[key] = fn;
					} else {
						delete config[key];
					}
				} catch (e) {
					console.warn(`Removed unparseable formatter for "${key}":`, config[key]);
					delete config[key];
				}
			} else if (typeof config[key] === 'object' && config[key] !== null) {
				config[key] = sanitizeHighchartsConfig(config[key]);
			}
		}

		return config;
	};

	const sendMessage = async (text: string) => {
		if (!text.trim() || isThinking) return;

		const userMessage: Message = {
			id: Date.now(),
			text,
			isUser: true,
			sender: 'user',
		};

		setMessages((prev) => [...prev, userMessage]);
		setIsThinking(true);

		try {
			const apiMessages: ChatMessage[] = [
				...messages.map(
					(m): ChatMessage => ({
						role: m.sender === 'user' ? 'user' : 'assistant',
						content: m.text,
					})
				),
				{ role: 'user', content: text },
			];

			let content: string | undefined;
			let inovResp: InovationChatResponse = {
				response: '',
				session_id: '',
				status: 'pending',
				trace: [],
			};

			if (useInnovationAI) {
				let payloadText = text;
				try {
					const chartTrigger =
						/\b(?:create(?: me)?(?: a)?)?\s*(?:pie|bar|line|area|scatter|bubble|combo)?\s*chart\b/i;
					if (chartTrigger.test(text)) {
						payloadText = `${text} 
									   -Please print me the highcharts json for what this chart would look like. 
									   -Always include a short summary of the chart and its data followed by the highchart json, 
									   do not show any label for the chart only show a label for the summary in the format of '📊 <b>Summary:</b> <br /><br /> <summary content>' and dont add any line breaks between the two`;
					}
				} catch (e) {
					// ignore regex errors
				}

				const userIdentifer = user.account + getEnvironmentCode();

				const inovationReq: InovationChatRequest = {
					message: payloadText,
					session_id: sessionId,
					include_reasoning: true,
					userId: userIdentifer,
				};

				inovResp = await ChatActions.sendInovationAIMessage(inovationReq);
				content =
					inovResp?.response ?? (typeof inovResp === 'string' ? inovResp : undefined);
			} else {
				const resp = await ChatActions.SendMessage({
					messages: apiMessages,
					context: context,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				});

				content =
					resp?.data?.response?.content ??
					resp?.data?.response ??
					(resp && resp.data && typeof resp.data === 'string' ? resp.data : undefined);
			}

			if (content) {
				const aiMessage: Message = {
					id: Date.now() + 1,
					text: content,
					isUser: false,
					sender: 'bot',
					reasoning: useInnovationAI
						? inovResp?.reasoning?.map((r: Reasoning) =>
								typeof r === 'string' ? { step: r, details: {} } : r
						  ) ?? []
						: undefined,
				};
				setMessages((prev) => [...prev, aiMessage]);

				// Handle possible AI-provided chart payloads - AWAIT this before finishing
				await (async () => {
					try {
						let chartObj: any = inovResp?.chart ?? null;
						let textBeforeLocal: string | undefined = undefined;
						if (
							!chartObj &&
							inovResp?.response &&
							typeof inovResp.response === 'string'
						) {
							const extractJSON = (
								text: string
							): { parsed: any; start: number; end: number } | null => {
								for (let i = 0; i < text.length; i++) {
									if (text[i] !== '{') continue;
									let depth = 0;
									for (let j = i; j < text.length; j++) {
										if (text[j] === '{') depth++;
										else if (text[j] === '}') depth--;
										if (depth === 0) {
											const candidate = text.substring(i, j + 1);
											try {
												const parsed = JSON.parse(candidate);
												return { parsed, start: i, end: j + 1 };
											} catch (e) {
												break;
											}
										}
									}
								}
								return null;
							};
							const parsedWithBounds = extractJSON(inovResp.response);
							if (
								parsedWithBounds &&
								parsedWithBounds.parsed &&
								parsedWithBounds.parsed.chart
							) {
								chartObj = parsedWithBounds.parsed;
								const before = inovResp.response
									.substring(0, parsedWithBounds.start)
									.trim();
								const cleanedBefore = before
									.replace(/```(?:json)?\s*$/i, '')
									.trim();
								textBeforeLocal = cleanedBefore;
							}
						}

						if (chartObj) {
							const safeChart = sanitizeHighchartsConfig(chartObj);
							setMessages((prev) =>
								prev.map((m) =>
									m.id === aiMessage.id
										? {
												...m,
												chart: safeChart,
												title: safeChart.title,
												text:
													textBeforeLocal && textBeforeLocal.length
														? textBeforeLocal
														: m.text,
										  }
										: m
								)
							);

							let foundDf: any = null;
							try {
								const allFrames = await Dataframe.GetAll();
								if (measures && measures.length > 0) {
									for (const df of allFrames) {
										const dfNames = (df.datasets || []).map((d: any) =>
											(d.title || '').toString().toLowerCase()
										);
										const measureNames = measures.map((m: any) =>
											(m.name || m.title || m.id || '')
												.toString()
												.toLowerCase()
										);
										const matches = measureNames.every((mn: string) =>
											dfNames.includes(mn)
										);
										if (matches) {
											foundDf = df;
											break;
										}
									}
								}
							} catch (err) {
								// ignore
							}

							if (foundDf) {
								setCurrentSelectedDataframe(foundDf);
							} else {
								const fakeId = Date.now();
								const fakeDf: any = {
									id: fakeId,
									folder: 0,
									name: `AI chart - ${
										chartObj.title?.text ?? chartObj.title ?? 'Chart'
									}`,
									created_at: '',
									creator: 0,
									updated_at: '',
									updater: 0,
									columnEntry: [],
									datasets: (chartObj.series || []).map(
										(s: any, idx: number) => ({
											id: fakeId + idx + 1,
											dataframe: fakeId,
											keyMeasureFact: 0,
											numDecimals: 2,
											title: s.name ?? `Series ${idx + 1}`,
										})
									),
									filters: [],
									order: [],
									rowEntry: [
										{
											id: fakeId + 1000,
											dataframe: fakeId,
											drillable: false,
											entity_id: 0,
											entity_type: 'Dimension',
											title: 'AI categories',
											totaled: false,
											use_sequence: false,
										},
									],
									type: 0,
									owner: 0,
									sharedDataframe: [],
								};
								setCurrentSelectedDataframe(fakeDf as any);
							}
						}
					} catch (err) {
						console.error('AI chart handling failed', err);
					}
				})();
			} else {
				const errorMessage: Message = {
					id: Date.now() + 1,
					text: 'Sorry, I encountered an error. Please try again.',
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			console.error('Send message error:', error);
			const errorMessage: Message = {
				id: Date.now() + 1,
				text: 'Sorry, I encountered an error. Please try again.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, errorMessage]);
		}
	};

	const handleSend = async () => {
		if (!input.trim() || isThinking) return;
		const text = input;
		setInput('');
		await sendMessage(text);
	};

	const doSend = () => void handleSend();

	useEffect(() => {
		const doAuto = async () => {
			if (
				autoSubmitMessage &&
				!hasAutoSubmittedRef.current &&
				!disabled &&
				!isThinking &&
				messages.length === 0
			) {
				hasAutoSubmittedRef.current = true;
				await sendMessage(autoSubmitMessage);
				onAutoSubmitted?.();
			}
		};
		void doAuto();
	}, [autoSubmitMessage, disabled, isThinking, messages.length, onAutoSubmitted]);

	const handleInputChange = (value: string) => {
		setInput(value);
		if (!hasInteractedRef.current && value.length > 0) {
			hasInteractedRef.current = true;
			onFirstInteraction?.();
		}
	};

	// External thread mode: only render the pinned input (messages rendered by parent)
	const containerClass = `chat-container ${useInnovationAI ? 'innovation-ai' : ''}`;

	if (externalThread) {
		return (
			<>
				<ChatInput
					input={input}
					setInput={handleInputChange}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModalOpen}
					hideSendButton={hideSend}
				/>
				<Modal
					variant={ModalVariant.large}
					title="AI * Insights"
					isOpen={isModalOpen}
					onClose={handleModalClose}
					className="chat-modal"
				>
					<div className={`${containerClass} chat-container-modal`}>
						<div
							className="chat-messages chat-messages-modal"
							ref={modalChatContainerRef}
						>
							{modalHeaderContent}
							<ChatMessages
								messages={messages}
								isThinking={isThinking}
							/>
							<div ref={messagesEndRef} />
						</div>
						<ChatInput
							input={input}
							setInput={setInput}
							onSend={doSend}
							isThinking={isThinking}
							isModalOpen={isModalOpen}
						/>
					</div>
				</Modal>
			</>
		);
	}

	// Legacy inline mode (kept for backward compatibility)
	const chatContent = (isModal: boolean) => (
		<>
			<div
				className={`chat-messages ${isModal ? 'chat-messages-modal' : ''}`}
				ref={isModal ? modalChatContainerRef : chatContainerRef}
			>
				<ChatMessages
					messages={messages}
					isThinking={isThinking}
					useInnovationAI={useInnovationAI}
				/>

				<div ref={messagesEndRef} />
			</div>

			<div className="chat-input-group">
				<ChatInput
					input={input}
					setInput={setInput}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModal}
				/>
			</div>
		</>
	);

	// Load saved conversation when sessionId changes
	useEffect(() => {
		const loadSavedConversation = async () => {
			if (sessionId) {
				try {
					// Fetch conversation using sessionId and jobId
					const response = await ChatActions.getInovationAIChatHistory(
						sessionId,
						String(contextId || '')
					);

					if (response && response.messages && response.messages.length > 0) {
						setIsThinking(true);

						// Map API response to Message format
						let loadedMessages: Message[] = response.messages.map(
							(msg: any, idx: number) => ({
								id: idx,
								text: msg.content || msg.text,
								isUser: msg.role === 'user',
								sender: msg.role === 'user' ? 'user' : 'bot',
								reasoning: msg.reasoning || undefined,
								chart: msg.chart || undefined,
							})
						);

						// Clean up user messages - remove chart instruction text
						const chartInstructionPattern =
							/-Please print me the highcharts json for what this chart would look like\.\s*-Always include a short summary of the chart and its data followed by the highchart json,\s*do not show any label for the chart only show a label for the summary in the format of '📊 <b>Summary:<\/b> <br \/><br \/> <summary content>' and dont add any line breaks between the two/g;

						loadedMessages = loadedMessages.map((msg) => ({
							...msg,
							text: msg.isUser
								? msg.text.replace(chartInstructionPattern, '').trim()
								: msg.text,
						}));

						// Extract charts from bot messages (same logic as sendMessage)
						loadedMessages = loadedMessages.map((msg) => {
							if (!msg.isUser && !msg.chart && msg.text) {
								const extractJSON = (
									text: string
								): { parsed: any; start: number; end: number } | null => {
									for (let i = 0; i < text.length; i++) {
										if (text[i] !== '{') continue;
										let depth = 0;
										for (let j = i; j < text.length; j++) {
											if (text[j] === '{') depth++;
											else if (text[j] === '}') depth--;
											if (depth === 0) {
												const candidate = text.substring(i, j + 1);
												try {
													const parsed = JSON.parse(candidate);
													return { parsed, start: i, end: j + 1 };
												} catch (e) {
													break;
												}
											}
										}
									}
									return null;
								};

								const parsedWithBounds = extractJSON(msg.text);
								if (
									parsedWithBounds &&
									parsedWithBounds.parsed &&
									parsedWithBounds.parsed.chart
								) {
									const before = msg.text
										.substring(0, parsedWithBounds.start)
										.trim();
									const cleanedBefore = before
										.replace(/```(?:json)?\s*$/i, '')
										.trim();

									return {
										...msg,
										chart: parsedWithBounds.parsed,
										title: parsedWithBounds.parsed.title,
										text: cleanedBefore || msg.text,
									};
								}
							}
							return msg;
						});

						setMessages(loadedMessages);

						// Scroll to bottom after loading
						setTimeout(() => scrollToBottom(), 100);

						// Disable thinking after messages load
						setIsThinking(false);
					} else {
						// No saved messages for this session.
						// If auto-submit has already populated (or is about to populate) messages,
						// leave them alone — the blank-slate case is handled by the contextId reset.
						if (!hasAutoSubmittedRef.current && !autoSubmitMessage) {
							setMessages([]);
							setInput('');
						}

						if (!autoSubmitMessage) {
							setIsThinking(false);
						}
					}
				} catch (error) {
					console.error('Error loading saved conversation:', error);
					if (!autoSubmitMessage) {
						setIsThinking(false);
					}
				}
			}
		};

		loadSavedConversation();
	}, [sessionId, autoSubmitMessage]);

	// Save chat after each bot response
	const saveChat = (messages: Message[]) => {
		if (!canSaveChat || messages.length < 2) return; // Need at least one exchange

		const currentSessionId = sessionId || String(Date.now());
		const currentJobId = String(contextId);

		// Generate title from first user message
		const firstUserMsg = messages.find((m) => m.isUser)?.text || 'Untitled';
		const title = firstUserMsg.substring(0, 50) + (firstUserMsg.length > 50 ? '...' : '');

		// Get first bot response as preview
		const preview = messages.find((m) => !m.isUser)?.text?.substring(0, 80);

		// Save to localStorage
		const savedChats: SavedChat[] = JSON.parse(localStorage.getItem('savedChats') || '[]');
		const existingIndex = savedChats.findIndex((c) => c.session_id === currentSessionId);

		const chatData: SavedChat = {
			session_id: currentSessionId,
			job_id: currentJobId,
			title,
			created_at: new Date().toISOString(),
			last_activity: new Date().toISOString(),
			numberOfMsgs: messages.length,
		};

		if (existingIndex >= 0) {
			savedChats[existingIndex] = chatData;
		} else {
			savedChats.push(chatData);
		}

		onSaveChat?.(currentSessionId, currentJobId, title);
	};

	// Call saveChat when messages update
	useEffect(() => {
		if (canSaveChat && messages.length > 1) {
			saveChat(messages);
		}
	}, [messages, canSaveChat]);

	return (
		<>
			<div className={containerClass}>
				{!isSinglePage && (
					<div className="chat-header">
						<small>
							<FontAwesomeIcon icon={faExpand} /> Follow up question? Prompt to get
							further insight...
						</small>
						<Button
							variant="plain"
							className="chat-expand-btn"
							onClick={() => setInternalShowModal(true)}
							title="Expand chat"
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</div>
				)}

				{chatContent(false)}
			</div>

			<Modal
				variant={ModalVariant.large}
				title="AI Chat"
				isOpen={internalShowModal}
				onClose={() => setInternalShowModal(false)}
				className="chat-modal"
			>
				<div className="chat-container chat-container-modal">{chatContent(true)}</div>
			</Modal>
		</>
	);
};

export default ChatInterface;
